<script>
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import cloneDeep from 'lodash/cloneDeep'
import { mask } from 'vue-the-mask'
import { datePickerFormat } from '@lib/utils/date-helper'
import { mapActions, mapGetters } from 'vuex'
import OfferMixin from '../../offer/offer.mixin'
import isPast from '@lib/utils/date-is-past'
import dateIsAfter from '@lib/utils/date-is-after'
import ValidationMixin from '@lib/mixins/validation'

import OffersPreview from './components/offers-preview'
import PreviewOfModal from './components/preview-of-modal'
import OfferCoupon from './components/offers/offer-coupon'
import OfferShow from './components/offers/offer-show'
import Editor from '@tinymce/tinymce-vue'
import { Base64 } from 'js-base64'

const initialData = {
  enabled: false,
  name: '',
  description: '',
  locations: [],
  endsAt: '',
  startsAt: '',
  terms: '',
  type: '',
  couponCode: '',
  couponType: '',
  promotionUrl: 'https://',
  redemptionImage: '',
  redirectPage: false,
  redirectContent: null,
  videoUrl: '',
  usageType: '',
  redemptionLimit: null,
  redemptionLimitPeriodTerm: null,
  redemptionLimitPeriodValue: null,
  uploadUrl: null,
  tags: [],
}

export default {
  name: 'RetailerOffersForm',
  directives: {
    mask,
  },
  components: {
    OffersPreview,
    PreviewOfModal,
    OfferCoupon,
    OfferShow,
    Editor,
  },
  mixins: [OfferMixin, ValidationMixin],
  props: {
    retailer: {
      type: Object,
      default: () => ({}),
    },
    offer: {
      type: Object,
      default: null,
    },
    showPreview: {
      type: Boolean,
      default: true,
    },
    offersCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    const validatorURL = (rule, value, callback) => {
      if (/^(https?:\/\/)/.test(value) !== true) {
        callback(new Error('Please include a http:// or https:// prefix on the URL entered'))
        return
      }

      if (value === 'https://' || value === 'http://') {
        callback(new Error('Promotion URL is required'))
        return
      }

      callback()
    }

    const validatorEditorMax = (rule, value, callback) => {
      const tempElement = document.createElement('div')
      tempElement.innerHTML = value

      if (tempElement.textContent.length > rule.max) {
        callback(new Error(`Terms and Conditions must be ${rule.max} characters or less`))
        return
      }

      callback()
    }

    return {
      tinyKey: process.env.VUE_APP_TINY_KEY,
      datePickerFormat,
      form: {},
      fileLoading: false,
      rules: {
        name: [
          {
            required: true,
            message: 'Offer name is required',
          },
          {
            max: 60,
            message: 'Offer name must be 60 characters or less',
          },
        ],
        description: [
          {
            required: true,
            message: 'Offer description is required',
          },
        ],
        terms: {
          max: 3000,
          validator: validatorEditorMax,
        },
        type: {
          required: true,
          message: 'Offer type is required',
        },
        usageType: {
          required: true,
          message: 'Redemption type is required',
        },
        redemptionImage: {
          required: true,
          message: 'Redemption image is required',
        },
        couponCode: [
          {
            required: true,
            message: 'Coupon code is required',
          },
          {
            max: 20,
            message: 'Coupon code must be 20 characters or less',
          },
        ],
        promotionUrl: [
          {
            required: true,
            message: 'Promotion URL is required',
          },
          {
            validator: validatorURL,
            trigger: 'change',
          },
        ],
        redirectContent: [
          {
            required: true,
            message: 'Redirect content is required',
          },
        ],
        videoUrl: [
          {
            validator: (rule, value, callback) => {
              if (!value) callback()

              if (this.resource === 'Youtube' && !/^(https:\/\/www.)((youtube.com)|(youtu.be))\/.+/.test(value)) {
                callback(new Error('Invalid Youtube video URL'))
              } else {
                callback()
              }
            },
            message: 'Invalid Youtube video URL',
            trigger: 'change',
          },
        ],
      },
      progress: false,
      showDeleteDialog: false,
      redemptionLimitPeriods: [
        {
          value: 'seconds',
          label: 'Seconds',
        },
        {
          value: 'minutes',
          label: 'Minutes',
        },
        {
          value: 'hours',
          label: 'Hours',
        },
        {
          value: 'days',
          label: 'Days',
        },
        {
          value: 'weeks',
          label: 'Weeks',
        },
        {
          value: 'months',
          label: 'Months',
        },
        {
          value: 'years',
          label: 'Years',
        },
      ],
      usageTypes: [
        {
          value: 'unique',
          label: 'Unique',
        },
        {
          value: 'generic',
          label: 'Generic',
        },
      ],
      offerTypes: [
        {
          value: 'show',
          label: 'Show and Save (Show to Redeem)',
        },
        {
          value: 'coupon',
          label: 'Coupon Code (Redeem at Checkout)',
        },
        {
          value: 'url',
          label: 'URL (Push to Website)',
        },
        {
          value: 'html',
          label: 'HTML',
        },
      ],
      minLogoDimensions: [860, 860],
      resource: 'Youtube',
      isVideoLinkExist: false,
      isShowPreviewFile: false,
      inputTagValue: '',
    }
  },
  computed: {
    ...mapGetters('me', [
      'canDeleteOffer',
    ]),
    isEdit() {
      return get(this.offer, 'id')
    },
    status() {
      return this.form.enabled ? 'Enabled' : 'Disabled'
    },
    statusRedirectPage() {
      return this.form.redirectPage ? 'Active' : 'Inactive'
    },
    offerComponent() {
      switch (this.form.type) {
        case 'show':
          return {
            name: 'offer-show',
            title: 'Show and Save',
            fullBody: true,
          }
        case 'coupon':
          return {
            name: 'offer-coupon',
            title: 'Coupon Code',
            fullBody: false,
          }
        default:
          return null
      }
    },
    startDatePickerOptions() {
      const { endsAt } = this.form

      return {
        disabledDate(date) {
          if (!endsAt) {
            return false
          }

          return dateIsAfter(date, endsAt)
        },
      }
    },
    endDatePickerOptions() {
      const { startsAt } = this.form

      return {
        disabledDate(date) {
          let isBeforeStartDate = false

          if (startsAt) {
            isBeforeStartDate = dateIsAfter(startsAt, date)
          }

          return isPast(date, true) || isBeforeStartDate
        },
      }
    },
    isFormChanged() {
      const locations = this.form.locations.filter(value => value)
      return !isEqual({ ...this.form, locations: locations }, this.offer)
    },
    isSaveBtnDisabled() {
      return (this.isEdit && !this.isFormChanged) || this.progress
    },
    logoLabel() {
      const label = ['Redemption Image']
      const { minLogoDimensions } = this
      if (minLogoDimensions && minLogoDimensions.length >= 2) {
        label.push(`(${minLogoDimensions[0]} x ${minLogoDimensions[1]} px minimum)`)
      }

      return label.join(' ')
    },
    isShowRedemptionLimit() {
      return this.form.type === 'coupon' && this.form.usageType === 'unique'
    },
  },
  watch: {
    'form.type'(_, oldVal) {
      if (this.isOfferTypeCoupon(this.form.type) && !this.form.couponType) {
        this.form.couponType = 'online'
      }

      if (oldVal) {
        if (!this.isOfferTypeShow(this.form.type)) {
          this.form.redemptionImage = ''
        }

        if (!this.isOfferTypeCoupon(this.form.type)) {
          this.form.couponCode = ''
          this.form.couponType = ''
          this.form.promotionUrl = 'https://'
        }

        if (!this.isOfferTypeUrl(this.form.type)) {
          this.form.promotionUrl = 'https://'
          this.form.redirectPage = false
          this.form.redirectContent = null
        }

        this.$nextTick(() => {
          this.$refs.form.clearValidate()
        })
      }
    },
    'form.usageType'(value) {
      if (value === 'unique') {
        this.form.couponCode = 'PLACEHOLDER'
      }
    },
    resource(newVal, oldVal) {
      this.form.videoUrl = ''
      this.isVideoLinkExist = false
      this.$refs.form.validate()
    },
    isVideoLinkExist(val) {
      this.isShowPreviewFile = val
    },
  },

  async created() {
    this.form = cloneDeep(initialData)

    if (this.isEdit) {
      this.form = Object.assign({}, this.form, cloneDeep(this.offer))
      this.isVideoLinkExist = !!this.form.videoUrl
    }

    if (!get(this.form, 'locations.length')) {
      this.form.locations.push(null)
    }
  },
  methods: {
    ...mapActions('offer', ['createOffer', 'updateOffer', 'deleteOffer', 'uploadCouponsFile']),
    changeCouponCode(value) {
      this.form.couponCode = value.toUpperCase()
    },
    addLocation() {
      this.form.locations.push('')
    },
    removeLocation(idx) {
      this.form.locations.splice(idx, 1)
    },
    async onSubmit() {
      this.clearBackendErrors()
      if (this.isSaveBtnDisabled) return

      const formValid = await this.$refs.form.validate().catch(() => false)
      if (!formValid) return

      this.progress = true

      const data = {
        ...this.form,
        locations: this.form.locations.filter(value => value),
        retailer: `/retailers/${this.retailer.id}`,
      }

      if (!this.isEdit) {
        Object.keys(data).forEach(field => {
          if (!data[field] || !data[field].length) {
            delete data[field]
          }
        })
      }

      if (!this.isOfferTypeShow(data.type)) {
        delete data.redemptionImage
      }

      if (!this.isOfferTypeCoupon(data.type)) {
        delete data.couponCode
        delete data.couponType
      }

      if (this.isOfferTypeCoupon(data.type)) {
        data.redemptionLimit = data.redemptionLimit ? Number(data.redemptionLimit) : null
        data.redemptionLimitPeriodValue = data.redemptionLimitPeriodValue ? Number(data.redemptionLimitPeriodValue) : null
      }

      if (this.isOfferTypeCoupon(data.type) && this.form.uploadUrl) {
        delete data.uploadUrl
      }

      if (
        !this.isOfferTypeUrl(data.type) &&
        !this.isCouponTypeOnlineOnly(data.couponType) &&
        !this.isCouponTypeOnlineStore(data.couponType)
      ) {
        delete data.promotionUrl
      }

      data.description = Base64.encode(data.description)
      data.terms = Base64.encode(data.terms)

      const options = { headers: { 'x-provider-id': this.retailer.providerId } }
      const response = await this[this.isEdit ? 'updateOffer' : 'createOffer']({
        data,
        options,
      })
      let [error, success] = response
      let errorMsg = null

      if (error === undefined && !success) {
        error = {}
        errorMsg = 'Something went wrong'
      }

      if (error) {
        this.processBackendErrors(error)
      }

      this.$notifier({
        response: [error, success],
        errorTitle: 'Unable to save offer',
        successTitle: 'Offer saved',
        successMsg: `${this.isEdit ? 'Changes' : 'New offer'} successfully saved.`,
        ...errorMsg && { errorMsg },
      }).then(success => {
        if (success) {
          this.$emit('cancel')
          this.$emit('update')
        }
      })

      this.progress = false
    },
    onCancel() {
      this.$nextTick(() => {
        this.$refs.form.clearValidate()
      })

      if (this.offersCount > 0) {
        this.$emit('cancel')

        this.form = this.offer ? cloneDeep(this.offer) : cloneDeep(initialData)
      } else {
        this.$router.push({
          name: 'retailer-details',
        })
      }
    },
    async onDelete() {
      this.showDeleteDialog = false
      this.progress = true

      await this.deleteOffer(this.form.id).then(response => {
        this.$notifier({
          response,
          errorTitle: 'Unable to delete offer',
          successTitle: 'Offer deleted',
          successMsg: 'Offer successfully deleted.',
        }).then(success => {
          if (success) {
            this.$emit('cancel')
            this.$emit('update')
          }
        })
      })

      this.progress = false
    },
    onDeleteClick() {
      this.form.videoUrl = ''
      this.isVideoLinkExist = false
    },
    checkLink(event) {
      const { value } = event.target
      if (!value || !/^(https?:\/\/.+)/.test(value)) return
      try {
        // eslint-disable-next-line no-unused-vars
        const url = new URL(value)
        this.isVideoLinkExist = true
      } catch (error) {
        console.error(error)
      }
    },

    async loadFileCsv(event) {
      this.fileLoading = true
      const file = event.target.files[0]
      const options = { headers: {
        'x-provider-id': this.retailer.providerId,
        'Content-Type': 'application/json',
      } }
      const data = {
        fileName: file.name,
        tag: `offerId=${this.offer ? this.offer.id : null}`,
      }
      const [error, success] = await this.uploadCouponsFile({ data, options })
      if (error) {
        const errorMsg = error.message
        this.$notifier({
          response: [error, success],
          errorTitle: 'Unable to load file',
          ...errorMsg && { errorMsg },
        })
        this.fileLoading = false
        return
      }

      const blobData = new Blob([file], { type: 'text/csv' })
      const xAmzTagging = get(success, 'requestHeaders[x-amz-tagging]', []).join('&')

      await fetch(success.uploadUrl, {
        method: 'PUT',
        body: blobData,
        headers: {
          'x-amz-tagging': xAmzTagging,
        },
      })

      this.form.uploadUrl = {
        url: success.uploadUrl,
        fileName: success.fileName,
      }
      this.fileLoading = false
    },

    deleteFileCsv() {
      this.form.uploadUrl = null
    },

    handleClose(tag) {
      this.form.tags.splice(this.form.tags.indexOf(tag), 1)
    },

    handleInputConfirm() {
      if (this.inputTagValue) {
        this.form.tags.push(this.inputTagValue)
      }
      this.inputTagValue = ''
    },
  },
}
</script>

<template>
  <div>
    <div :class="$style.formWrapper">
      <el-form
        ref="form"
        :model="form"
        :rules="rules"
        label-position="top"
        :class="['card-form card-form--wide', $style.form]"
      >
        <el-form-item
          label="Enabled"
          prop="enabled"
        >
          <el-switch
            v-model="form.enabled"
            active-color="#13ce66"
            inactive-color="#a7a7a7"
            :active-value="true"
            :inactive-value="false"
          />
          <span :class="$style.status">
            {{ status }}
          </span>
        </el-form-item>
        <el-form-item
          label="Offer Name"
          prop="name"
          :error="backendErrors.name"
        >
          <el-input v-model="form.name" />
        </el-form-item>

        <el-form-item
          label="Offer Type"
          prop="type"
          :error="backendErrors.type"
        >
          <el-select
            v-model="form.type"
            placeholder="Select an offer type"
          >
            <el-option
              v-for="item in offerTypes"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="form.type === 'coupon'"
          label="Redemption Type"
          prop="usageType"
          :error="backendErrors.usageType"
        >
          <el-select
            v-model="form.usageType"
            placeholder="Select redemption type"
          >
            <el-option
              v-for="item in usageTypes"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="isShowRedemptionLimit"
          label="Redemption limit"
          prop="redemptionLimit"
          :error="backendErrors.redemptionLimit"
        >
          <el-input
            v-model="form.redemptionLimit"
            type="number"
          />
        </el-form-item>

        <el-form-item
          v-if="isShowRedemptionLimit"
          label="Redemption Period"
          prop="redemptionLimitPeriodTerm"
          :error="backendErrors.redemptionLimitPeriodTerm"
        >
          <el-select
            v-model="form.redemptionLimitPeriodTerm"
            placeholder="Select redemption limit period"
          >
            <el-option
              v-for="item in redemptionLimitPeriods"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="isShowRedemptionLimit"
          label="Redemption limit period value"
          prop="redemptionLimitPeriodValue"
          :error="backendErrors.redemptionLimitPeriodValue"
        >
          <el-input
            v-model="form.redemptionLimitPeriodValue"
            type="number"
          />
        </el-form-item>


        <el-form-item
          label="Offer Description"
          prop="description"
          :error="backendErrors.description"
        >
          <editor
            v-model="form.description"
            :api-key="tinyKey"
            :init="{
              resize: false,
              statusbar: true,
              menubar: false,
              height: '500',
              width: '100%',
              plugins: [
                'advlist', 'autolink', 'lists', 'link', 'charmap', 'print', 'preview',
                'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'table', 'paste', 'help', 'wordcount',
                'pagebreak', 'emoticons', 'codesample', 'table', 'image'
              ],
              toolbar:
                'undo redo | formatselect | bold italic underline strikethrough | forecolor backcolor removeformat |\
                fontselect fontsizeselect | alignleft aligncenter alignright alignjustify | link | table | insertdatetime |\
                bullist numlist outdent indent | pagebreak | code codesample |\
                charmap emoticons | image|\
                fullscreen preview print | wordcount | help',
              toolbar_mode: 'sliding',
              image_advtab: true,
            }"
          />
        </el-form-item>
        <el-form-item
          label="Participating Locations"
          prop="locations"
        >
          <div :class="$style.fullwidth">
            <div
              v-for="(location, index) in form.locations"
              :key="index"
              :class="$style.locationItem"
            >
              <address-autocomplete v-model="form.locations[index]" />
              <el-button
                v-if="form.locations && form.locations.length > 1"
                type="info"
                icon="el-icon-close"
                circle
                size="mini"
                :class="$style.locationItemBtn"
                @click="removeLocation(index)"
              />
            </div>
            <el-button
              type="text"
              icon="el-icon-plus"
              @click="addLocation"
            >
              Add location
            </el-button>
          </div>
        </el-form-item>
        <el-form-item
          :label="`Offer Start Date ${form.startsAt ? '(Offer will not be displayed until the start date)' : ''}`"
        >
          <el-date-picker
            v-model="form.startsAt"
            v-mask="['##/##/####']"
            type="date"
            format="dd/MM/yyyy"
            :value-format="datePickerFormat"
            placeholder="Enter Date"
            :picker-options="startDatePickerOptions"
          />
        </el-form-item>
        <el-form-item label="Offer End Date">
          <el-date-picker
            v-model="form.endsAt"
            v-mask="['##/##/####']"
            type="date"
            format="dd/MM/yyyy"
            :value-format="datePickerFormat"
            placeholder="Enter Date"
            :picker-options="endDatePickerOptions"
          />
        </el-form-item>
        <el-form-item
          label="Offer Terms and Conditions"
          prop="terms"
          :error="backendErrors.terms"
        >
          <editor
            v-model="form.terms"
            :api-key="tinyKey"
            :init="{
              resize: false,
              statusbar: true,
              menubar: false,
              height: '500',
              width: '100%',
              plugins: [
                'advlist', 'autolink', 'lists', 'link', 'charmap', 'print', 'preview',
                'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'table', 'paste', 'help', 'wordcount',
                'pagebreak', 'emoticons', 'codesample', 'table', 'image'
              ],
              toolbar:
                'undo redo | formatselect | bold italic underline strikethrough | forecolor backcolor removeformat |\
                fontselect fontsizeselect | alignleft aligncenter alignright alignjustify | link | table | insertdatetime |\
                bullist numlist outdent indent | pagebreak | code codesample |\
                charmap emoticons | image|\
                fullscreen preview print | wordcount | help',
              toolbar_mode: 'sliding',
              image_advtab: true,
            }"
          />
        </el-form-item>

        <template v-if="isOfferTypeUrl(form.type)">
          <el-form-item
            label="Redirect Page"
            prop="active"
          >
            <el-switch
              v-model="form.redirectPage"
              active-color="#13ce66"
              inactive-color="#a7a7a7"
              :active-value="true"
              :inactive-value="false"
            />
            <span :class="$style.status">
              {{ statusRedirectPage }}
            </span>
          </el-form-item>
          <el-form-item
            v-if="form.redirectPage"
            label="Redirect Content"
            prop="redirectContent"
            :error="backendErrors.redirectContent"
          >
            <the-textarea
              v-model="form.redirectContent"
              :class="$style.fullwidth"
            />
          </el-form-item>
        </template>

        <template v-if="isOfferTypeShow(form.type)">
          <el-form-item
            :label="logoLabel"
            prop="redemptionImage"
            :error="backendErrors.redemptionImage"
          >
            <hub-image-uploader
              v-model="form.redemptionImage"
              crop="1:1"
              :dimensions-min="minLogoDimensions"
            />
          </el-form-item>
        </template>
        <template v-if="isOfferTypeCoupon(form.type)">
          <el-form-item
            v-if="form.usageType === 'generic'"
            label="Coupon Code"
            prop="couponCode"
            :error="backendErrors.couponCode"
          >
            <el-input
              :value="form.couponCode"
              @input="changeCouponCode"
            />
          </el-form-item>
          <el-form-item
            v-if="form.usageType === 'unique' && isEdit"
            prop="uploadUrl"
            :error="backendErrors.uploadUrl"
          >
            <div :class="$style.fileUploader">
              <p class="el-form-item__label">
                Coupon Codes File *
              </p>
              <input
                v-if="!form.uploadUrl"
                id="fileLoader"
                ref="fileLoader"
                :class="$style.hidden"
                :disabled="form.uploadUrl || fileLoading"
                type="file"
                accept=".csv"
                @input="loadFileCsv"
              >
              <div
                :class="$style.fileUploader"
              >
                <div v-if="form.uploadUrl">
                  File with coupon codes was added
                </div>
                <div :class="$style.fileUploaderWrapper">
                  <div
                    class="el-button el-button--button"
                    :class="{
                      'is-disabled': form.uploadUrl || fileLoading,
                      [$style.upload]: true,
                    }"
                    @click="$refs.fileLoader && $refs.fileLoader.click()"
                  >
                    <div
                      :class="$style.uploadPlus"
                    >
                      <span>
                        +
                      </span>
                    </div>
                    <span>Upload file</span>
                  </div>
                  <base-loader
                    v-if="fileLoading"
                    :class="$style.fileLoader"
                  />
                </div>
                <div
                  v-if="form.uploadUrl"
                >
                  <div
                    :class="$style.file"
                  >
                    <div :class="$style.fileName">
                      File: {{ form.uploadUrl.fileName }}
                    </div>
                    <div :class="$style.fileDeleteIcon">
                      <el-button
                        icon="el-icon-delete"
                        size="small"
                        :class="$style.red"
                        circle
                        @click="deleteFileCsv"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </el-form-item>
          <el-form-item>
            <el-radio-group
              v-model="form.couponType"
              prop="couponType"
              :class="$style.radioGroup"
            >
              <el-radio
                label="online"
                border
                :class="$style.radio"
              >
                Online only
              </el-radio>
              <el-radio
                label="in_store"
                border
                :class="$style.radio"
              >
                In-Store only
              </el-radio>
              <el-radio
                label="both"
                border
                :class="$style.radio"
              >
                Online and In-Store
              </el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item
            v-if="isCouponTypeOnlineOnly(form.couponType) || isCouponTypeOnlineStore(form.couponType)"
            label="Offer URL"
            prop="promotionUrl"
            :error="backendErrors.promotionUrl"
          >
            <el-input v-model="form.promotionUrl" />
          </el-form-item>
        </template>
        <template v-if="isOfferTypeUrl(form.type)">
          <el-form-item
            label="URL"
            prop="promotionUrl"
            :error="backendErrors.promotionUrl"
          >
            <el-input v-model="form.promotionUrl" />
          </el-form-item>
        </template>
        <template>
          <el-form-item
            label="Video from Youtube or file"
            :class="$style.mb0"
          >
            <el-radio-group v-model="resource">
              <el-radio
                label="Youtube"
              />
              <el-radio label="File" />
            </el-radio-group>
          </el-form-item>
          <div
            v-if="form.videoUrl && isVideoLinkExist"
          >
            <div
              :class="$style.file"
            >
              <div :class="$style.fileName">
                URL: {{ form.videoUrl }}
              </div>
              <div :class="$style.fileDeleteIcon">
                <el-button
                  icon="el-icon-delete"
                  size="small"
                  :class="$style.red"
                  circle
                  @click="onDeleteClick()"
                />
              </div>
            </div>
          </div>
          <el-form-item
            prop="videoUrl"
            :error="backendErrors.videoUrl"
            :class="$style.videoUploader"
          >
            <div
              :class="$style.videoWrapper"
            >
              <el-input
                v-if="resource === 'Youtube'"
                v-model="form.videoUrl"
                type="url"
                :disabled="isVideoLinkExist"
                placeholder="https://www.youtube.com/..."
                @blur="checkLink"
              />
              <hub-video-uploader
                v-if="resource === 'File'"
                v-model="form.videoUrl"
                :disabled="isVideoLinkExist"
              />
            </div>
          </el-form-item>
          <!-- Tags -->
          <el-form-item
            label="Tags"
          >
            <el-input
              v-model="inputTagValue"
              @keyup.enter.native="handleInputConfirm"
              @blur="handleInputConfirm"
            />
          </el-form-item>

          <div :class="$style.tags">
            <el-tag
              v-for="tag in form.tags"
              :key="tag"
              :class="$style.tag"
              closable
              :disable-transitions="false"
              @close="handleClose(tag)"
            >
              {{ tag }}
            </el-tag>
          </div>
        </template>
        <div class="card-form__footer">
          <div v-if="isEdit && canDeleteOffer">
            <el-button
              type="danger"
              :disabled="progress"
              @click.stop="showDeleteDialog = true"
            >
              Delete Offer
            </el-button>
          </div>
          <div>
            <el-button
              :disabled="progress"
              @click="onCancel"
            >
              Cancel
            </el-button>
            <el-button
              type="primary"
              :disabled="isSaveBtnDisabled"
              @click="onSubmit"
            >
              Save Offer
            </el-button>
          </div>
        </div>
      </el-form>

      <div
        v-if="showPreview"
        :class="$style.previewWrapper"
      >
        <div :class="$style.previewTitle">
          Preview
        </div>
        <offers-preview
          :retailer="{ ...retailer }"
          :offers="[form]"
          :class="$style.preview"
        />
        <template v-if="form.terms">
          <hr :class="['divider-primary', $style.divider]">
          <div :class="$style.previewTitle">
            Preview
          </div>
          <preview-of-modal title="Terms and Conditions">
            <div v-html="form.terms" />
            <span slot="footer">
              <el-button type="primary">
                Got it
              </el-button>
            </span>
          </preview-of-modal>
        </template>
        <template v-if="offerComponent">
          <hr :class="['divider-primary', $style.divider]">
          <div :class="$style.previewTitle">
            Preview
          </div>
          <preview-of-modal
            :title="offerComponent.title"
            :full-body="offerComponent.fullBody"
          >
            <component
              :is="offerComponent.name"
              :offer="form"
            />
          </preview-of-modal>
        </template>
      </div>
    </div>

    <state-dialog
      :visible.sync="showDeleteDialog"
      title="Delete Offer"
      brand="danger"
    >
      <el-icon
        slot="icon"
        name="price-tag"
      />
      <el-button
        slot="subicon"
        icon="el-icon-delete"
        type="danger"
        circle
      />
      <div :class="$style.msg">
        Are you sure you wish to delete this offer?
      </div>
      <div class="modal__footer">
        <el-button
          type="danger"
          class="el-button--wide"
          @click="onDelete"
        >
          Delete offer
        </el-button>
      </div>
    </state-dialog>
  </div>
</template>

<style lang="scss" module>
  .status {
    margin-left: rem(14px);
  }

  .fullwidth {
    width: 100%;
  }

  .uploadPlus {
    display: inline-flex;
    margin-right: rem(4px);
    transform: scale(1.6);
  }

  .locationItem {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: rem(20px);
  }

  .locationItemBtn {
    padding: rem(2px) !important;
    margin-left: rem(20px);
    font-size: rem(16px);
    background: #c0c4cc;
    border-color: #c0c4cc;
  }

  .radioGroup {
    margin: rem(-10px);
  }

  .radio {
    margin: rem(10px);
  }

  .previewWrapper {
    width: calc(100% - 30rem);
    margin-left: rem(100px);
    user-select: none;
  }

  .preview {
    pointer-events: none;
    transform: scale(0.8);
    transform-origin: left top;
  }

  .previewTitle {
    margin-bottom: rem(15px);
    font-size: rem(20px);
    font-weight: 600;
  }

  .divider {
    margin: rem(20px) 0;
    background-image: linear-gradient(to left, transparent 50%, var(--color-dark-gray) 50%);
  }

  .formWrapper {
    display: flex;
    justify-content: space-between;
  }

  .form {
    flex-basis: 100%;
  }

  .msg {
    text-align: center;
    white-space: nowrap;
  }

  .subicon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    font-size: 16px;
    color: var(--color-primary-text);
    background-color: var(--color-error);
    border-radius: 50%
  }

  .videoWrapper {
    width: 100%;
  }

  .mb0 {
    margin-bottom: 0;
  }

  .file {
    display: flex;
    align-items: center;
    margin-top: 0.2rem;
    margin-bottom: 1rem;
  }

  .fileName {
    font-weight: bold;
  }

  .fileDeleteIcon {
    margin-left: 1rem;
  }

  .red {
    color: red;
  }

input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
input[type='number'] {
    -moz-appearance: textfield;
}
.uploadPlus {
  display: inline-flex;
  margin-right: rem(4px);
  transform: scale(1.6);
}
.hidden {
  display: none;
}
.fileUploader {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.fileUploaderWrapper {
  display: flex;
}
.fileLoader {
  margin-left: rem(15px);
}
.upload {
  background: #FFFFFF;
  border-color: #0086ff;
  color: #0086ff;
}

.tags {
  display: flex;
  flex-wrap: wrap;
}

.tag {
  margin-right: 10px;
  margin-bottom: 10px;
}
</style>
